import { AxiosResponse } from 'axios';
import {
  AdvancedSearchResponse,
  AggregationResponse
} from 'src/components/BeaconV2Components/utils/AdvancedSearchParser/types';
import {
  isAdvancedSearchResponseObject,
  isAxiosResponse
} from 'src/components/BeaconV2Components/utils/AdvancedSearchParser/utils';

/**
 * Creates an instance of the AdvancedSearchParser around the response(s) from an advanced search request.
 * @param advancedSearchResponse  The response from the advanced search request. Can be a single response, an array of responses, or an axios response.
 * @returns An instance of the AdvancedSearchParser.
 * @example
 * const response = await axios.post('api/advancedSearch', requestBody);
 * const parser = new AdvancedSearchParser(response);
 * const documents = parser.getDocuments();
 * const metrics = parser.parseAggregations(reducerFunction)
 */
export default class AdvancedSearchParser {
  /**
   * The response(s) from the advanced search request. Responses will always be an array of AdvancedSearchResponse objects.
   */
  private responses: AdvancedSearchResponse[] = [];

  /**
   * A collection of all aggregations from the response(s).
   */
  private aggregations: AggregationResponse = {};

  /**
   * A collection of all documents from the response(s).
   */
  private documents: any[] = [];

  /**
   * Determines if the AdvancedSearchParser has been initialized with valid responses.
   */
  private validated: boolean = false;

  public constructor(
    advancedSearchResponse:
      | AxiosResponse<AdvancedSearchResponse[]> // Single axios response with multiple AS responses
      | AxiosResponse<AdvancedSearchResponse[]>[] // Multiple axios responses with multiple AS responses
      | AdvancedSearchResponse // Single AS response
      | AdvancedSearchResponse[] // Multiple AS responses
  ) {
    this.initialize(advancedSearchResponse);
  }

  /**
   * Initializes the parser with the response(s) from the API request.
   * @param advancedSearchResponse  The response from the advanced search request. Can be a single response, an array of responses, or an axios response.
   * @returns void
   */
  private initialize(advancedSearchResponse: unknown) {
    try {
      // Check the response(s) for validity and set the responses if valid.
      this.validate(advancedSearchResponse);

      // If the responses are valid, combine the aggregations and documents.
      if (this.validated) {
        // Combine aggregations
        this.combineAggregations();

        // Combine documents
        this.combineDocuments();
      }
    } catch (err) {
      console.error(`[AdvancedSearchParser] Error initializing parser: ${err}`);
      this.validated = false;
    }
  }

  private validate(advancedSearchResponse) {
    try {
      // Check for initial conditions that would prevent parsing
      if (
        !advancedSearchResponse || // Null or undefined
        (Array.isArray(advancedSearchResponse) && advancedSearchResponse.length === 0) || // Empty array
        (Array.isArray(advancedSearchResponse) && advancedSearchResponse.some((element) => !element)) || // Empty element in array
        typeof advancedSearchResponse !== 'object' // Not an object or array
      ) {
        this.validated = false;
        return;
      }

      // Create a collection of AS response objects
      let collection = [];

      // Handle multiple axios or AS responses
      if (Array.isArray(advancedSearchResponse)) {
        advancedSearchResponse.forEach((response) => {
          if (isAxiosResponse(response)) {
            collection = [...collection, ...response.data];
          } else if (isAdvancedSearchResponseObject(response)) {
            collection = [...collection, response];
          }
        });
      } else if (isAxiosResponse(advancedSearchResponse)) {
        // Handle single axios response
        collection = advancedSearchResponse?.data ?? [];
      } else {
        // Handle single response
        collection = [advancedSearchResponse];
      }

      // Validate the collection of responses to ensure they are all AS response objects
      const validResponses = collection.every((response) => {
        if (typeof response !== 'object') {
          console.error(`[AdvancedSearchParser] Expected type "object" but received type "${typeof response}"`);
          return false;
        } else {
          if (Array.isArray(response)) {
            console.error('[AdvancedSearchParser] Expected type "object" but received type "array"');
            return false;
          } else if (!isAdvancedSearchResponseObject(response)) {
            return false;
          }
          return true;
        }
      });

      if (validResponses) {
        this.responses = collection;
        this.validated = true;
      } else {
        this.validated = false;
      }
    } catch {
      this.validated = false;
    }
  }

  // *** Aggregation-type Logic *** //

  /**
   * Combines all aggregations from the response(s) into a single object.
   */
  private combineAggregations() {
    this.aggregations = this.responses.reduce((combined, response) => {
      if (response?.aggregations) {
        Object.keys(response.aggregations).forEach((key) => {
          if (!combined[key]) {
            combined[key] = [];
          }
          combined[key].push(...response.aggregations[key]);
        });
      }
      return combined;
    }, {} as { [key: string]: any[] });
  }

  public getAggregations() {
    return this.aggregations;
  }

  public parseAggregations(func: (aggregation: AggregationResponse) => any) {
    return func(this.aggregations);
  }

  // *** Document-type Logic *** //

  private combineDocuments() {
    this.documents = this.responses.reduce((documentList, response) => {
      if (response?.documents) {
        documentList.push(...response.documents);
      }
      return documentList;
    }, []);
  }

  public getDocuments() {
    return this.documents;
  }

  public parseDocuments(func: (document: any) => any) {
    return func(this.documents);
  }
}
